import { Fragment } from 'react';
import { Path } from '@react-pdf/renderer';
import { values } from 'lodash';

/**
* @function Partitions
* @description Renders partition walls for rooms and areas in a PDF document using SVG paths.
* Supports both container-level and area-level walls with optional dashed lines for missing walls.
*
* @param {Object} props - Component properties
* @param {boolean} props.inContainer - Whether rendering container-level partitions
* @param {string} props.areaId - ID of specific area to render, if any
* @param {number} props.scale - Scale factor for dimensions
* @param {Object} props.content - Room and area data
* @param {Object} props.content.dimensions - Container dimensions
* @param {Object} props.content.areas - Areas data by ID
* @param {Object} props.content.areas[].dimensions - Area dimensions
* @param {Object} props.content.areas[].location - Area position
* @param {Object} props.content.areas[].surroundingWalls - Wall presence flags
* @returns {React.Component} Fragment containing SVG path elements for walls
*
* @example
* <Partitions
*   inContainer={true}
*   scale={0.5}
*   content={{
*     dimensions: {length: 100, width: 80},
*     areas: {
*       area1: {
*         dimensions: {length: 50, width: 40},
*         location: {left: 0, top: 0},
*         surroundingWalls: {top: true, right: false}
*       }
*     }
*   }}
* />
*
* @note
* - Uses @react-pdf/renderer for PDF rendering
* - Walls are solid lines by default, dashed when missing
* - Supports both container bounds and internal area partitions
* - Coordinates adjusted by scale factor for PDF output
*/
const Partitions = (props) => {
  const { inContainer, areaId, scale, content: { dimensions, areas } } = props;
  const rectangles = [...(inContainer ? [dimensions] : []), ...(areaId ? [areas?.[areaId]] : values(areas)).map(a => ({ ...a.dimensions, ...a.location, walls: a.surroundingWalls }))];
  const lines = rectangles.flatMap(rect => {
    const { top: tw = true, right: rw = true, bottom: bw = true, left: lw = true } = rect.walls || {};
    return [...Array(2)].flatMap((x, index) => {
      const odd = index % 2;
      const sign = odd ? -1 : 1;
      const x0 = 1 + (inContainer * (rect.left || 0) + odd * rect.length) * scale;
      const y0 = 1 + (inContainer * (rect.top || 0) + odd * rect.width) * scale;
      const x1 = x0 + sign * rect.length * scale;
      const y1 = y0 + sign * rect.width * scale;
      return [{ d: `m${x0} ${y0}H${x1}`, dash: odd ? bw : tw }, { d: `m${x0} ${y0}V${y1}`, dash: odd ? rw : lw }]
    });
  });
  return (
    <Fragment>
      {lines.map((line, index) => <Path key={`line-${index}`} {...line} stroke="#000" strokeWidth={1.5} strokeDasharray={line.dash ? 'none' : '12'} />)}
    </Fragment>
  )
};

export default Partitions;